import type { StarterEntry } from "../../ops";

export const MARKETING_TEMPLATE_CATEGORIES = [
  { id: "welcome_onboarding", label: "Welcome & Onboarding" },
  { id: "subscription_lifecycle", label: "Subscription" },
  { id: "family_vault", label: "Family Vault" },
  { id: "family_hub", label: "Family Hub" },
  { id: "gift_subscriptions", label: "Gift Subscriptions" },
  { id: "engagement_conversion", label: "Engagement & Conversion" },
  { id: "content_product", label: "Content & Product" },
  { id: "marketing_campaigns", label: "Marketing & Campaigns" },
] as const;

/** Category tabs for template picker / templates list (includes All + legacy DB values). */
export const MARKETING_TEMPLATE_TABS = [
  { id: "all", label: "All", legacyMatch: [] as string[] },
  { id: "welcome_onboarding", label: "👋 Welcome & Onboarding", legacyMatch: ["engagement"] },
  { id: "subscription_lifecycle", label: "💳 Subscription", legacyMatch: ["subscription"] },
  { id: "family_vault", label: "🔒 Family Vault", legacyMatch: ["collaboration"] },
  { id: "family_hub", label: "👨‍👩‍👧 Family Hub", legacyMatch: ["collaboration"] },
  { id: "gift_subscriptions", label: "🎁 Gift Subs", legacyMatch: [] as string[] },
  { id: "engagement_conversion", label: "🔥 Engagement & Conversion", legacyMatch: ["engagement"] },
  { id: "content_product", label: "📖 Content & Product", legacyMatch: ["notification"] },
  { id: "marketing_campaigns", label: "📣 Marketing & Campaigns", legacyMatch: ["marketing"] },
] as const;

export function templateMatchesCategory(
  rowCategory: string,
  tabId: string,
): boolean {
  if (tabId === "all") return true;
  const tab = MARKETING_TEMPLATE_TABS.find((t) => t.id === tabId);
  if (!tab) return rowCategory === tabId;
  return (
    rowCategory === tabId ||
    (tab.legacyMatch?.includes(rowCategory) ?? false) ||
    API_TO_UI_CATEGORY[rowCategory] === tabId
  );
}

/** Normalize GET /api/admin/email-templates list response (paginated shape). */
export function parseEmailTemplatesListResponse(res: {
  data?: unknown;
  templates?: unknown;
}): Record<string, unknown>[] {
  const d = res?.data;
  if (Array.isArray(d)) return d as Record<string, unknown>[];
  if (d && typeof d === "object" && Array.isArray((d as { templates?: unknown }).templates)) {
    return (d as { templates: Record<string, unknown>[] }).templates;
  }
  if (Array.isArray(res?.templates)) {
    return res.templates as Record<string, unknown>[];
  }
  return [];
}

export function isMarketingNurtureTemplate(row: {
  kind?: string | null;
  category?: string;
}): boolean {
  return (
    row.kind === "nurture" ||
    (row.kind == null &&
      !["authentication", "administrative", "transactional"].includes(
        row.category ?? "",
      ))
  );
}

/** Maps v2 category slugs to API-accepted legacy values when needed. */
export const CATEGORY_TO_API: Record<string, string> = {
  welcome_onboarding: "engagement",
  subscription_lifecycle: "subscription",
  family_vault: "collaboration",
  family_hub: "collaboration",
  gift_subscriptions: "subscription",
  engagement_conversion: "engagement",
  content_product: "notification",
  marketing_campaigns: "marketing",
  engagement: "engagement",
  subscription: "subscription",
  collaboration: "collaboration",
  marketing: "marketing",
  notification: "notification",
  transactional: "transactional",
  administrative: "administrative",
  authentication: "authentication",
};

/** Map DB legacy category back to a UI tab id for the editor dropdown. */
export const API_TO_UI_CATEGORY: Record<string, string> = {
  engagement: "engagement_conversion",
  subscription: "subscription_lifecycle",
  collaboration: "family_vault",
  marketing: "marketing_campaigns",
  notification: "content_product",
  transactional: "marketing_campaigns",
  administrative: "marketing_campaigns",
  authentication: "marketing_campaigns",
};

export function categoryForApi(uiCategory: string): string {
  return CATEGORY_TO_API[uiCategory] || uiCategory;
}

export interface MarketingTemplateForm {
  template_key: string;
  template_name: string;
  description: string;
  category: string;
  subject_line: string;
  html_content: string;
  plain_text_content: string;
  preview_text: string;
  from_name: string;
  from_email: string;
  reply_to_email: string;
  status: "draft" | "active" | "inactive";
  is_active: boolean;
}

export const EMPTY_MARKETING_TEMPLATE_FORM: MarketingTemplateForm = {
  template_key: "",
  template_name: "",
  description: "",
  category: "marketing_campaigns",
  subject_line: "",
  html_content: "",
  plain_text_content: "",
  preview_text: "",
  from_name: "LifeScribe",
  from_email: "no-reply@trylifescribe.com",
  reply_to_email: "support@trylifescribe.com",
  status: "draft",
  is_active: false,
};

export const DEFAULT_MARKETING_HTML = `<!DOCTYPE html>
<html lang="en">
<head><meta charset="utf-8"><meta name="viewport" content="width=device-width, initial-scale=1"></head>
<body style="font-family: Arial, sans-serif; line-height: 1.6; color: #1B1F23; max-width: 600px; margin: 0 auto; padding: 24px;">
  <p>Hi {{user_name}},</p>
  <p>Write your marketing message here.</p>
  <p style="margin-top: 32px; font-size: 12px; color: #666;">{{unsubscribe_url}}</p>
</body>
</html>`;

export function extractMergeTags(...parts: (string | undefined)[]): string[] {
  const tags = new Set<string>();
  const re = /\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g;
  for (const part of parts) {
    if (!part) continue;
    let m: RegExpExecArray | null;
    while ((m = re.exec(part)) !== null) tags.add(m[1]);
  }
  return [...tags];
}

export function stripHtml(html: string): string {
  return html
    .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, "")
    .replace(/<[^>]+>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

export function formFromStarter(starter: StarterEntry): MarketingTemplateForm {
  const subject =
    (starter.subjectScaffold as string) ||
    (starter.subjectHint as string) ||
    "";
  const preview =
    (starter.previewTextScaffold as string) ||
    (starter.previewText as string) ||
    "";
  const category =
    (starter.category as string) || "marketing_campaigns";
  const html =
    (starter.html_content as string) || DEFAULT_MARKETING_HTML;
  return {
    ...EMPTY_MARKETING_TEMPLATE_FORM,
    template_key: String(starter.id || "custom_template"),
    template_name: String(starter.name || "New Template"),
    description: String(starter.useCase || starter.description || ""),
    category,
    subject_line: subject,
    preview_text: preview,
    html_content: html,
    plain_text_content:
      (starter.plain_text_content as string) || preview || stripHtml(html),
  };
}

export function toApiPayload(form: MarketingTemplateForm) {
  const tags = extractMergeTags(
    form.subject_line,
    form.html_content,
    form.plain_text_content,
    form.preview_text,
  );
  const sample: Record<string, string> = {};
  for (const t of tags) {
    sample[t] = t === "user_name" ? "Sample User" : `[${t}]`;
  }
  return {
    template_key: form.template_key.trim(),
    template_name: form.template_name.trim(),
    description: form.description.trim() || form.template_name.trim(),
    category: categoryForApi(form.category),
    kind: "nurture",
    subject_line: form.subject_line.trim(),
    html_content: form.html_content,
    plain_text_content: form.plain_text_content || stripHtml(form.html_content),
    preview_text: form.preview_text,
    required_variables: tags,
    optional_variables: [] as string[],
    sample_data: sample,
    from_name: form.from_name.trim(),
    from_email: form.from_email.trim(),
    reply_to_email: form.reply_to_email.trim() || form.from_email.trim(),
    status: form.status,
    is_active: form.is_active,
  };
}

export function formFromApiRow(row: Record<string, unknown>): MarketingTemplateForm {
  return {
    template_key: String(row.template_key ?? ""),
    template_name: String(row.template_name ?? ""),
    description: String(row.description ?? ""),
    category:
      API_TO_UI_CATEGORY[String(row.category ?? "")] ||
      String(row.category ?? "marketing_campaigns"),
    subject_line: String(row.subject_line ?? ""),
    html_content: String(row.html_content ?? ""),
    plain_text_content: String(row.plain_text_content ?? ""),
    preview_text: String(row.preview_text ?? ""),
    from_name: String(row.from_name ?? "LifeScribe"),
    from_email: String(row.from_email ?? "no-reply@trylifescribe.com"),
    reply_to_email: String(row.reply_to_email ?? "support@trylifescribe.com"),
    status: (row.status as MarketingTemplateForm["status"]) || "draft",
    is_active: Boolean(row.is_active),
  };
}
